/**
 * EducationalPageRules.ts
 * Created: 2024-03-20
 * Purpose: Defines the structure, styling, and patterns for educational pages in the Construction Learning Roadmap
 */

export interface EducationalPageSection {
  title: string;
  content: string[];
  subsections?: {
    title: string;
    content: string[];
  }[];
}

export interface EducationalPageTemplate {
  route: string;
  title: string;
  sections: EducationalPageSection[];
}

export const educationalPageStyles = {
  colors: {
    primary: '#9575cd',
    background: 'rgba(0, 0, 0, 0.6)',
    text: '#ffffff',
    border: 'rgba(149, 117, 205, 0.2)',
    hover: 'rgba(149, 117, 205, 0.4)'
  },
  spacing: {
    containerMaxWidth: '1200px',
    sectionPadding: '2rem',
    contentPadding: '1.5rem'
  },
  typography: {
    title: {
      size: '2.5rem',
      weight: '600',
      marginBottom: '2rem'
    },
    sectionTitle: {
      size: '1.8rem',
      weight: '500',
      marginBottom: '1.5rem'
    },
    subTitle: {
      size: '1.4rem',
      weight: '500',
      marginBottom: '1rem'
    }
  }
};

export const educationalPageStructure = {
  requiredComponents: [
    'PageContainer',
    'BackButton',
    'ContentContainer',
    'Title',
    'Section',
    'SectionTitle',
    'SubTitle',
    'Paragraph',
    'List',
    'ListItem'
  ],
  layout: {
    backButtonPosition: {
      construction: {
        top: '120px',
        left: '2rem',
        returnPath: '/education/construction',
        returnState: {
          selectedCategory: 'Site Development', // Category containing Earthwork
          autoExpand: true
        }
      },
      default: {
        top: '2rem',
        left: '2rem',
        returnPath: '/education'
      }
    },
    contentAlignment: 'center',
    backgroundImage: 'constructionBackgroundImg',
    backgroundProperties: {
      size: 'cover',
      position: 'center',
      overlay: 'rgba(0, 0, 0, 0.4)'
    }
  },
  contentStructure: {
    introduction: {
      required: true,
      description: 'Overview of the topic'
    },
    scope: {
      required: true,
      description: 'Applications and boundaries of the topic'
    },
    process: {
      required: true,
      description: 'Step-by-step breakdown'
    },
    keyConsiderations: {
      required: true,
      description: 'Important factors and best practices'
    },
    practicalAspects: {
      required: false,
      description: 'Cost estimation, timeline, resources'
    }
  }
};

// Example usage:
export const earthworkPageTemplate: EducationalPageTemplate = {
  route: '/education/construction/earthwork',
  title: 'Earthwork in Construction',
  sections: [
    {
      title: 'Introduction',
      content: [
        'Earthwork is a fundamental aspect of construction that involves...'
      ]
    },
    // Additional sections would be defined here
  ]
}; 